// Saves and broadcasts a change that undo/redo applied (D-036), with the REST bodies and socket events the app
// already sends for the same kind of edit. Edges are removed before nodes and created after them.

const differs = (a, b) => JSON.stringify(a) !== JSON.stringify(b);

const nodePayload = (node, documentId) => ({
    creatorId: node.creatorId,
    id: node.id,
    documentId,
    name: node.data?.label ?? '',
    type: node.type,
    position: node.position,
    data: node.data,
});

const edgePayload = (edge, documentId) => ({
    id: edge.id,
    documentId,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle,
    targetHandle: edge.targetHandle,
});

// The size a node shows: its saved style, else the size kept in data, else its type's default size.
const sizeOf = (node, defaultSize) => {
    if (node.style?.width && node.style?.height) return { width: node.style.width, height: node.style.height };
    if (node.data?.width && node.data?.height) return { width: node.data.width, height: node.data.height };
    return defaultSize(node.type);
};

// One socket event per changed field: remote clients apply only the first field of a `node-update`.
const nodeEditCalls = (before, after, documentId, defaultSize) => {
    const { id } = after;
    const calls = [];
    if (differs(before.position, after.position)) {
        calls.push({ body: { position: after.position }, event: { id, position: after.position, documentId } });
    }
    if (differs(before.style, after.style)) {
        const style = sizeOf(after, defaultSize);
        if (style) {
            calls.push({
                body: { style },
                event: { id, nodePosition: { ...style, ...after.position }, style, documentId },
            });
        }
    }
    const typeChanged = before.type !== after.type;
    if (differs(before.data, after.data) || typeChanged) {
        const was = before.data ?? {};
        const now = after.data ?? {};
        const events = [];
        if (was.label !== now.label) events.push({ id, name: now.label ?? '', documentId });
        if (was.nodeBg !== now.nodeBg) events.push({ id, bg: now.nodeBg, documentId });
        if (was.textBg !== now.textBg) events.push({ id, textBg: now.textBg, documentId });
        if (typeChanged) events.push({ id, type: after.type, documentId });
        calls.push({ body: typeChanged ? { data: now, type: after.type } : { data: now }, events });
    }
    return calls;
};

/**
 * `diff`: the change just applied (`before` → `after` per element). `snapshot`: the graph after it. `service`: the
 * map service (template documents get one template sync instead of per-element calls). `emit`: socket emitters.
 */
export const saveGraphDiff = async ({ diff, snapshot, documentId, service, emit, defaultSize }) => {
    const useRest = !service.isTemplateDocument;
    const removedEdges = diff.edges.filter((change) => !change.after);
    const removedNodes = diff.nodes.filter((change) => !change.after);
    const addedNodes = diff.nodes.filter((change) => !change.before);
    const addedEdges = diff.edges.filter((change) => !change.before);
    const editedNodes = diff.nodes.filter((change) => change.before && change.after);

    await Promise.all(removedEdges.map(({ id }) => {
        emit.edgeDelete({ selectedEdgeId: id, documentId });
        return useRest && service.deleteEdge(id, snapshot);
    }));
    await Promise.all(removedNodes.map(({ id }) => {
        emit.nodeDelete({ selectedNodeId: id, documentId });
        return useRest && service.deleteNode(id, snapshot);
    }));
    await Promise.all(addedNodes.map(async ({ after }) => {
        const payload = nodePayload(after, documentId);
        emit.nodeAdd(payload);
        if (!useRest) return;
        await service.createNode(payload, snapshot);
        if (after.style?.width && after.style?.height) {
            await service.updateNode(after.id, { style: { width: after.style.width, height: after.style.height } }, snapshot);
        }
    }));
    await Promise.all(addedEdges.map(({ after }) => {
        const payload = edgePayload(after, documentId);
        emit.edgeAdd(payload);
        return useRest && service.createEdge(payload, snapshot);
    }));
    await Promise.all(editedNodes.flatMap(({ before, after }) =>
        nodeEditCalls(before, after, documentId, defaultSize).map(({ body, event, events }) => {
            (events ?? [event]).forEach((payload) => emit.nodeUpdate(payload));
            return useRest && service.updateNode(after.id, body, snapshot);
        })
    ));
    if (!useRest) await service.syncTemplate(snapshot);
};
